import Invest from "src/components/Invest";
import { FC } from "react";
import { Col, Row, Typography, message } from "antd";
import { CircleArrowRight01Icon } from "@hugeicons/core-free-icons";
import HugeIcon from "src/components/HugeIcon";
import { CurrencySymbolLookup } from "src/utils/constants/currency";
import { Wrapper } from "./style";
import { useBuyTickerOrder } from "../../api/useBuyTickerOrder";
import { useSellStockOrder } from "../../api/useSellTickerOrder";
import { sideOrder } from "../../utils/constants";
import { fixNumber } from "src/utils/number";
import { useQueryClient } from "react-query";
import {
  Button as UIButton,
  DialogActions,
  DialogFooter,
} from "src/components/ui";
import { colors } from "src/theme/colors";
import styled from "styled-components";
import { text } from "src/theme/ui";
import OtpModal from "src/components/Modals/Otp";
import NiceModal from "@ebay/nice-modal-react";

interface SummaryModalProps {
  setSummaryModalVisible: any;
  isSummaryModalVisible: boolean;
  onCancel: () => void;
  order: any;
  /**
   * Render as a STEP of the dialog that opened it, not as a dialog of its
   * own.
   *
   * Reviewing an order used to stack a second dialog over the first: two
   * scrims, two headers, the form still visible behind and unreachable, and
   * two Cancel buttons a few pixels apart doing different things. Placing a
   * trade is one task with two steps, and a step belongs inside the dialog
   * doing the task.
   */
  inline?: boolean;
}

const { Paragraph, Title } = Typography;

const DialogDescription = styled.p`
  margin: 0 0 16px;
  font-size: 12.5px;
  line-height: 1.55;
  color: ${colors.gray[500]};
`;

/* The step's own name, where the dialog's title cannot change: the shell is
   still titled after the order being placed, which is what the reader is
   doing in both steps. */
const StepHeading = styled.h3`
  ${text.hero}
  margin: 0 0 4px;
  font-size: 15px;
  color: ${colors.gray[900]};
`;

const DialogSubject = styled.h3`
  ${text.hero}
  margin: 0 0 10px;
  font-size: 15px;
  color: ${colors.gray[900]};
`;

export const SummaryModal: FC<SummaryModalProps> = ({
  setSummaryModalVisible,
  isSummaryModalVisible,
  order,
  onCancel,
  inline,
}) => {
  const data = { ...order.formData, ...order.otherData };

  const queryClient = useQueryClient();

  const {
    mutateAsync: sendBuyOrder,
    isLoading: isBuyOrderLoading,
    isSuccess: isBuyOrderSuccess,
  } = useBuyTickerOrder();

  const {
    mutateAsync: sendSellOrder,
    isLoading: isSellOrderLoading,
    isSuccess: isSellOrderSuccess,
  } = useSellStockOrder();

  const handleResendOtp = async () => {
    if (data.order_side === sideOrder.BUY)
      return sendBuyOrder({ payload: order.formData, otpCode: null });
    else return sendSellOrder({ payload: order.formData, otpCode: null });
  };

  const handleSubmitOtp = async (otp: string) => {
    if (data.order_side === sideOrder.BUY)
      return sendBuyOrder({ payload: order.formData, otpCode: otp });
    else return sendSellOrder({ payload: order.formData, otpCode: otp });
  };

  const handleOtpSuccess = async () => {
    message.success(`${data.order_side} order placed successfully`);
    queryClient.invalidateQueries({ queryKey: ["brokerageOrders"] });
    setSummaryModalVisible(false);
    onCancel();
  };

  const handleOtpFailure = async () => {
    message.error(`${data.order_side} order failured !`);
  };

  const handleConfirmOrder = async () => {
    try {
      await handleResendOtp();
      NiceModal.show(OtpModal, {
        resend: handleResendOtp,
        verify: handleSubmitOtp,
        onSuccess: handleOtpSuccess,
        onFailure: handleOtpFailure,
      });
    } catch (error) {
      message.error("something went wrong !");
    }
  };

  const currencySymbol = CurrencySymbolLookup[data.currency];

  const rows = [
    {
      key: "order_side",
      label: "Side",
      prefix: "",
      suffix: "",
      color: colors.success[600],
    },
    { key: "order_type", label: "Type", prefix: "", suffix: "", color: "" },
    {
      key: "order_quantity",
      label: "Quantity",
      prefix: "",
      suffix: "",
      color: "",
    },
    {
      key: "order_price",
      label: "Price",
      prefix: currencySymbol,
      suffix: "",
      color: "",
    },
    {
      key: "order_value",
      label: "Value",
      prefix: currencySymbol,
      suffix: "",
      color: "",
    },
    {
      key: "order_limit_price",
      label: "Limit Price",
      prefix: currencySymbol,
      suffix: "",
      color: "",
    },
    { key: "tif", label: "TIF", prefix: "", suffix: "", color: "" },
    // { key: 'order_stop_price', label: 'Stop Price', prefix: currencySymbol, suffix: "", color: "" },
    // { key: 'duration', label: 'Order Duration', prefix: "", suffix: "", color: "" },
    // { key: 'take_profit', label: 'Take Profit', prefix: "+", suffix: "%", color: "" },
    // { key: 'stop_loss', label: 'Stop Loss', prefix: "-", suffix: "%", color: "" },
  ];

  const FIRST_SUBTITLE = `${
    process.env.REACT_APP_BANK_NAME || ""
  } will add the corresponding Management Fees.`;
  const SECONDE_SUBTITLE = "We will send you a copy of this summary via email";

  const actions = (
    <DialogActions>
      <UIButton
        variant="secondary"
        size="default"
        onClick={() => setSummaryModalVisible(false)}
      >
        {inline ? "Back" : "Cancel"}
      </UIButton>
      <UIButton
        variant="primary"
        size="default"
        onClick={handleConfirmOrder}
        loading={
          data.order_side === sideOrder.SELL
            ? isSellOrderLoading
            : isBuyOrderLoading
        }
      >
        Confirm
        <HugeIcon icon={CircleArrowRight01Icon} />
      </UIButton>
    </DialogActions>
  );

  const body = (
    <div>
      {/* Left, and in an ink step rather than a half-transparent black —
          which lands on whatever happens to be behind it. */}
      <DialogDescription>
        {FIRST_SUBTITLE} {SECONDE_SUBTITLE}
      </DialogDescription>
      <Wrapper>
        <DialogSubject>{`${data["ticker_symbol"]} · ${data["ticker_name"]}`}</DialogSubject>
        {rows.map(
          (row) =>
            data[row.key] !== undefined && (
              <Row gutter={[16, 16]} key={row.key}>
                <Col span={12} style={{ color: colors.gray[600] }}>
                  {row.label}
                </Col>
                <Col
                  span={12}
                  style={{
                    textAlign: "right",
                    fontWeight: "600",
                    color: row.color,
                  }}
                >
                  {row.prefix}
                  {typeof data[row.key] === "string"
                    ? data[row.key].replace(
                        /^./,
                        data[row.key][0].toUpperCase(),
                      )
                    : typeof data[row.key] === "number"
                    ? fixNumber(data[row.key], 3)
                    : data[row.key]}
                  {row.suffix}
                </Col>
              </Row>
            ),
        )}
      </Wrapper>
    </div>
  );

  /**
   * A step of the dialog that opened it: the body takes the place of the
   * form and the actions go into the shell's own footer band, through the
   * slot every flow in this product uses for actions of its own.
   */
  if (inline) {
    return (
      <>
        <StepHeading>Order summary</StepHeading>
        {body}
        <DialogFooter>{actions}</DialogFooter>
      </>
    );
  }

  return (
    <Invest.Modal
      title="Order Summary"
      closable={false}
      handleCancel={() => setSummaryModalVisible(false)}
      isModalVisible={isSummaryModalVisible}
      handleOk={() => console.log()}
      /* The actions are the shell's footer band — the body scrolls, and a
         row at the end of it is a row below the fold. */
      footer={actions}
    >
      {body}
    </Invest.Modal>
  );
};
